import { intersection } from 'lodash';
import { DecisionType } from './decision';
import { ApplicationDetailedLifecycle } from './detailedLifecyle';

/**
 * Class for Supported Nora User Permissions
 */
export class Roles {
  public static ADMIN = 'UW_ADMIN';
  public static CASEMANAGER = 'UW_CASEMANAGER';
  public static UNDERWRITER = 'UW_UNDERWRITER';
  public static PRODUCTION_UNDERWRITER = 'UW_PRODUCTION_UNDERWRITER';
  public static CHIEF_UNDERWRITER = 'UW_CHIEF_UNDERWRITER';
  public static CHIEF_CASEMANAGER = 'UW_CHIEF_CASEMANAGER';
  public static UWX_TEAM = 'UW_UWX_TEAM';
  public static READ_ONLY = 'UW_READ_ONLY';
  public static UW_FLEX_UNDERWRITER = 'UW_FLEX_UNDERWRITER';
  public static CARRIER_UNDERWRITER = 'CARRIER_UNDERWRITER';
  public static CHIEF_CARRIER_UNDERWRITER = 'CHIEF_CARRIER_UNDERWRITER';
  public static LEGAL_CARRIER_UNDERWRITER = 'LEGAL_CARRIER_UNDERWRITER';
  public static UNDERWRITER_COPILOT = 'UNDERWRITER_COPILOT';
  public static REFERRAL = 'UW_REFERRAL';
  public static UW_LIAISON = 'UW_LIAISON';
  public static UW_JUNIOR_UNDERWRITER = 'UW_JUNIOR_UNDERWRITER';
  public static UW_STRATEGY_REVIEWER = 'UW_STRATEGY_REVIEWER';
  public static UW_UNDERWRITING_MANAGER = 'UW_UNDERWRITING_MANAGER';
  public static UW_PA_ADMIN = 'UW_PA_ADMIN';

  // v2 Roles
  public static WORKBENCH_ADMIN = 'WORKBENCH_ADMIN';
  public static WORKBENCH_UWA = 'WORKBENCH_UWA';
  public static WORKBENCH_SENIOR_OPS_UNDERWRITER =
    'WORKBENCH_SENIOR_OPS_UNDERWRITER';
  public static WORKBENCH_STRATEGY_REVIEWER = 'WORKBENCH_STRATEGY_REVIEWER';
  public static WORKBENCH_UNDERWRITER = 'WORKBENCH_UNDERWRITER';
  public static WORKBENCH_JUNIOR_UNDERWRITER = 'WORKBENCH_JUNIOR_UNDERWRITER';
  public static WORKBENCH_READ_ONLY = 'WORKBENCH_READ_ONLY';
  public static WORKBENCH_CARRIER_UNDERWRITER = 'WORKBENCH_CARRIER_UNDERWRITER';
  public static WORKBENCH_CARRIER_CHIEF_UNDERWRITER =
    'WORKBENCH_CARRIER_CHIEF_UNDERWRITER';
  public static WORKBENCH_CARRIER_LEGAL_UNDERWRITER =
    'WORKBENCH_CARRIER_LEGAL_UNDERWRITER';
}

export const NonCarrierRoles = [
  Roles.ADMIN,
  Roles.CASEMANAGER,
  Roles.UNDERWRITER,
  Roles.PRODUCTION_UNDERWRITER,
  Roles.CHIEF_UNDERWRITER,
  Roles.CHIEF_CASEMANAGER,
  Roles.UWX_TEAM,
  Roles.READ_ONLY,
  Roles.UW_FLEX_UNDERWRITER,
  Roles.REFERRAL,
  Roles.UW_LIAISON,
  Roles.UW_JUNIOR_UNDERWRITER,
  Roles.UW_STRATEGY_REVIEWER,
  Roles.UW_UNDERWRITING_MANAGER,
  Roles.WORKBENCH_ADMIN,
  Roles.WORKBENCH_UWA,
  Roles.WORKBENCH_SENIOR_OPS_UNDERWRITER,
  Roles.WORKBENCH_STRATEGY_REVIEWER,
  Roles.WORKBENCH_UNDERWRITER,
  Roles.WORKBENCH_JUNIOR_UNDERWRITER,
  Roles.WORKBENCH_READ_ONLY,
];

export const LegacyCarrierRoles = [
  Roles.CARRIER_UNDERWRITER,
  Roles.CHIEF_CARRIER_UNDERWRITER,
  Roles.LEGAL_CARRIER_UNDERWRITER,
];

/**
 * Mirrors backend `NoraRole`: the v1 entries of `NonCarrierRoles`, the v1 carrier roles and `UW_PA_ADMIN`
 * (which the backend grants decision permissions, but `NonCarrierRoles` here does not list). A new v1 role must
 * be added to the backend enum and here. roles.test.ts checks this set against a hand-copied list of the
 * backend values; it does not import the backend, so update that list too.
 */
const V1_ROLES = new Set<string>([
  ...NonCarrierRoles.filter(role => !role.startsWith('WORKBENCH_')),
  ...LegacyCarrierRoles,
  Roles.UW_PA_ADMIN,
]);

/**
 * True only for a v1 role in the backend `NoraRole` enum, which includes the v1 carrier roles. An exact match,
 * not a `UW_` prefix test: the Okta groups claim also carries `UW_*` roles Nora does not own
 * (e.g. `UW_CONFIG_MAINTAINER`, added by default for UW IQ users), which must not count as v1.
 */
export function isV1NoraRole(role: string): boolean {
  return V1_ROLES.has(role);
}

export const CarrierRoles = [
  // v1 roles
  ...LegacyCarrierRoles,
  // v2 roles
  Roles.WORKBENCH_CARRIER_UNDERWRITER,
  Roles.WORKBENCH_CARRIER_CHIEF_UNDERWRITER,
  Roles.WORKBENCH_CARRIER_LEGAL_UNDERWRITER,
];

export const NoraPermissionV2 = {
  // General - Tabs and Pages
  VIEW_OVERVIEW: 'VIEW_OVERVIEW',
  VIEW_QA_CASE: 'VIEW_QA_CASE',
  EDIT_QA_CASE: 'EDIT_QA_CASE',
  // Decisions
  MAKE_DECISION: 'MAKE_DECISION',
  MAKE_PRELIMINARY_DECISION: 'MAKE_PRELIMINARY_DECISION',
  EDIT_DECISION_LEADING_TO_CLR: 'EDIT_DECISION_LEADING_TO_CLR',
  TRIGGER_CLR: 'TRIGGER_CLR',
  INITIATE_REINSTATEMENT: 'INITIATE_REINSTATEMENT',
  // Carrier
  SMART_CASE_ASSIGNMENT: 'SMART_CASE_ASSIGNMENT',
  MAKE_INITIAL_AUDIT_DECISION: 'MAKE_INITIAL_AUDIT_DECISION',
  MAKE_FINAL_AUDIT_DECISION: 'MAKE_FINAL_AUDIT_DECISION',
  MAKE_LEGAL_DECISION: 'MAKE_LEGAL_DECISION',
  // Tasks / tabs
  VIEW_TASKS_MANAGEMENT: 'VIEW_TASKS_MANAGEMENT',
  OUTDATE_TASK: 'OUTDATE_TASK',
  UPLOAD_LETTERS: 'UPLOAD_LETTERS',
  ORDER_TASK: 'ORDER_TASK',
  COMPLETE_TASK: 'COMPLETE_TASK',
  // Application details
  VIEW_PII_DATA: 'VIEW_PII_DATA',
  REQUEST_RULE_REVIEW: 'REQUEST_RULE_REVIEW',
  INITIATE_POST_ISSUE_AUDIT: 'INITIATE_POST_ISSUE_AUDIT',
  // Evidence
  VIEW_HIPAA_AUTH_FORM: 'VIEW_HIPAA_AUTH_FORM',
  VIEW_POLICY_COMBINED_DOC: 'VIEW_POLICY_COMBINED_DOC',
  VIEW_DOWNLOAD_RAW_EVIDENCE: 'VIEW_DOWNLOAD_RAW_EVIDENCE',
  // Notes
  DELETE_NOTES: 'DELETE_NOTES',
  CAN_ADD_NOTES_ON_LOCKED_CASE: 'CAN_ADD_NOTES_ON_LOCKED_CASE',
  // Assignments
  REASSIGN_CASE_MANAGER: 'REASSIGN_CASE_MANAGER',
  // Admin
  VIEW_CONTROL_PANEL: 'VIEW_CONTROL_PANEL',
  // 2026 features
  CREATE_CASE_REFERRAL: 'CREATE_CASE_REFERRAL',
  VIEW_CASE_REFERRAL: 'VIEW_CASE_REFERRAL',
  HALT_CLR: 'HALT_CLR',
} as const;

export const CarrierPermission = {
  MAKE_INITIAL_AUDIT_DECISION: 'MAKE_INITIAL_AUDIT_DECISION',
  MAKE_FINAL_AUDIT_DECISION: 'MAKE_FINAL_AUDIT_DECISION',
  MAKE_LEGAL_DECISION: 'MAKE_LEGAL_DECISION',
} as const;

export const RoleDetailedLifeCycleMap = new Map<string, string>([
  // v1 carrier roles
  [
    Roles.CARRIER_UNDERWRITER,
    ApplicationDetailedLifecycle.CARRIER_AUDIT_REVIEW,
  ],
  [
    Roles.CHIEF_CARRIER_UNDERWRITER,
    ApplicationDetailedLifecycle.FINAL_AUDIT_REVIEW,
  ],
  [
    Roles.LEGAL_CARRIER_UNDERWRITER,
    ApplicationDetailedLifecycle.HOLDING_FOR_RESCISSION,
  ],
  // v2 carrier roles
  [
    Roles.WORKBENCH_CARRIER_UNDERWRITER,
    ApplicationDetailedLifecycle.CARRIER_AUDIT_REVIEW,
  ],
  [
    Roles.WORKBENCH_CARRIER_CHIEF_UNDERWRITER,
    ApplicationDetailedLifecycle.FINAL_AUDIT_REVIEW,
  ],
  [
    Roles.WORKBENCH_CARRIER_LEGAL_UNDERWRITER,
    ApplicationDetailedLifecycle.HOLDING_FOR_RESCISSION,
  ],
]);

export const RoleDecisionTypeMap = new Map<string, DecisionType>([
  // v1 carrier roles
  [Roles.CARRIER_UNDERWRITER, DecisionType.INITIAL_AUDIT],
  [Roles.CHIEF_CARRIER_UNDERWRITER, DecisionType.FINAL_AUDIT],
  [Roles.LEGAL_CARRIER_UNDERWRITER, DecisionType.LEGAL_CHECK],
  // v2 carrier roles
  [Roles.WORKBENCH_CARRIER_UNDERWRITER, DecisionType.INITIAL_AUDIT],
  [Roles.WORKBENCH_CARRIER_CHIEF_UNDERWRITER, DecisionType.FINAL_AUDIT],
  [Roles.WORKBENCH_CARRIER_LEGAL_UNDERWRITER, DecisionType.LEGAL_CHECK],
]);

export const ACCESS_TOKEN_KEY = 'accessToken';

export function getCarrierRole(roles: string[]): string | undefined {
  const output = intersection(roles, CarrierRoles);
  return output.length > 0 ? output[0] : undefined;
}

export function getCarrierRoles(roles: string[]): string[] | undefined {
  const output = intersection(roles, CarrierRoles);
  return output.length > 0 ? output : undefined;
}

export function isNonCarrierRolePresent(roles: string[]): boolean {
  const output = intersection(roles, NonCarrierRoles);
  return output.length > 0;
}

export function isUnderwriterCopilot(roles: string[]) {
  return roles.includes(Roles.UNDERWRITER_COPILOT);
}

export function isUnderwriterUWX(roles: string[]) {
  return roles.includes(Roles.UWX_TEAM);
}

export function isJuniorUnderwriter(roles: string[]) {
  return roles.includes(Roles.UW_JUNIOR_UNDERWRITER);
}

export function resolveCarrierRole(
  roles: string[],
  detailedLifecycle: string,
): string | undefined {
  return CarrierRoles.find(
    role =>
      roles?.includes(role) &&
      RoleDetailedLifeCycleMap.get(role) === detailedLifecycle,
  );
}

/** Lifecycle → carrier decision type (for Admin / non-carrier roles with audit perms). */
export const LifecycleDecisionTypeMap = new Map<string, DecisionType>([
  [
    ApplicationDetailedLifecycle.CARRIER_AUDIT_REVIEW,
    DecisionType.INITIAL_AUDIT,
  ],
  [
    ApplicationDetailedLifecycle.FINAL_AUDIT_REVIEW,
    DecisionType.FINAL_AUDIT,
  ],
  [
    ApplicationDetailedLifecycle.HOLDING_FOR_RESCISSION,
    DecisionType.LEGAL_CHECK,
  ],
]);

/**
 * Resolve carrier audit decisionType from carrier role when present;
 * otherwise derive from detailed lifecycle (WORKBENCH_ADMIN / sheet Admin).
 */
export function resolveCarrierDecisionType(
  roles: string[],
  detailedLifecycle: string,
): DecisionType | undefined {
  const carrierRole = resolveCarrierRole(roles, detailedLifecycle);
  if (carrierRole) {
    return RoleDecisionTypeMap.get(carrierRole);
  }
  return LifecycleDecisionTypeMap.get(detailedLifecycle);
}
